import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { RefreshCw, Users, Clock, Phone, Mail, Stethoscope, CalendarDays, AlertCircle, Tag, FlaskConical, CheckCircle2, XCircle, ClipboardList, Zap, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";

const API = window.location.origin + "/api";

const RANGE_OPTIONS = [
  { days: 2, label: "Oggi + Domani" },
  { days: 7, label: "Prossimi 7 giorni" },
  { days: 14, label: "Prossimi 14 giorni" },
  { days: 30, label: "Prossimi 30 giorni" },
  { days: 90, label: "Prossimi 3 mesi" },
  { days: 180, label: "Prossimi 6 mesi" },
  { days: 365, label: "Prossimi 12 mesi" },
];

function formatDate(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return dateStr;
  }
}

function formatTime(timeStr) {
  if (!timeStr) return "--:--";
  try {
    return new Date(timeStr).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "--:--";
  }
}

function GroupCard({ group, onDirectTest, taggedKey }) {
  const groupKey = `${group.contact_id}|${group.date}`;
  const isTagged = taggedKey === groupKey;
  return (
    <div
      className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden hover:border-amber-300 transition-colors"
      data-testid={`duplicate-group-${group.contact_id}-${group.date}`}
    >
      {/* Header: patient info */}
      <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <h3 className="text-base font-bold text-slate-800 truncate" data-testid="patient-name">
              {group.patient_name}
            </h3>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-200/80 px-2 py-0.5 rounded-full uppercase tracking-wider">
              <AlertCircle className="w-3 h-3" />
              {group.count} app.ti
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            {group.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {group.phone}
              </span>
            )}
            {group.email && (
              <span className="flex items-center gap-1 truncate max-w-[200px]">
                <Mail className="w-3 h-3" />
                <span className="truncate">{group.email}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => onDirectTest && onDirectTest(group)}
            data-testid={`btn-direct-test-${group.contact_id}-${group.date}`}
            title="Apri test invio diretto pre-compilato con gli appuntamenti REALI di questo paziente"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[#1a1fa8] text-[#1a1fa8] bg-white hover:bg-[#1a1fa8] hover:text-white transition-colors"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Test diretto
          </button>
          {isTagged && (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border bg-emerald-100 border-emerald-300 text-emerald-700"
              data-testid={`btn-tagged-indicator-${group.contact_id}-${group.date}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Inviato
            </span>
          )}
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-white border border-amber-200 px-2.5 py-1.5 rounded-lg flex-shrink-0">
            <CalendarDays className="w-3.5 h-3.5" />
            {formatDate(group.date)}
          </div>
        </div>
      </div>

      {/* Appointments list */}
      <div className="divide-y divide-slate-100">
        {group.appointments.map((a, _i) => (
          <div key={a.id} className="px-5 py-3 grid grid-cols-[70px_1fr_auto] items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-sm font-bold text-slate-800 tabular-nums">
                {formatTime(a.startTime)}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-sm text-slate-700 truncate">{a.title || "—"}</div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 max-w-[220px]">
              <Stethoscope className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{a.doctor_name || "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MatchingAppuntamentiPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [days, setDays] = useState(2);
  // Audit log multiappuntamento (storico tag inviati)
  const [tagLog, setTagLog] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [recentlyTagged, setRecentlyTagged] = useState(null); // "contactId|date"
  const [verifyDialog, setVerifyDialog] = useState(null); // { open, patient, contactId, value, hasTag, error, loading, customFieldSet }
  // Direct test (independent from real groups)
  const [directTestOpen, setDirectTestOpen] = useState(false);
  const [directSearch, setDirectSearch] = useState("");
  const [directResults, setDirectResults] = useState([]);
  const [directSearching, setDirectSearching] = useState(false);
  const [directSelected, setDirectSelected] = useState(null); // {id, name}
  const [directCustomValue, setDirectCustomValue] = useState("");
  const [directSubmitting, setDirectSubmitting] = useState(false);
  const intervalRef = useRef(null);
  const abortRef = useRef(null);
  const directSearchTimer = useRef(null);
  const [forceScanning, setForceScanning] = useState(false);
  // Diagnostica per nome paziente / contact_id GHL: usa endpoint
  // `/api/multiappt/diagnose` (già esistente) per spiegare PERCHÉ il
  // cron non ha applicato i tag a un paziente specifico. Risposta
  // include events_in_day + verdict + log_entries recenti.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagQuery, setDiagQuery] = useState("");
  const [diagDate, setDiagDate] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const handleDiagnose = useCallback(async () => {
    const q = (diagQuery || "").trim();
    if (!q) {
      toast.error("Inserisci il nome del paziente o il contact_id");
      return;
    }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const params = { q };
      if (diagDate) params.date = diagDate;
      const { data } = await axios.get(`${API}/multiappt/diagnose`, {
        params, withCredentials: true, timeout: 60000,
      });
      setDiagResult(data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Errore diagnostica — riprova",
      );
    } finally {
      setDiagLoading(false);
    }
  }, [diagQuery, diagDate]);

  // Forza scansione cron immediata: chiamata manuale all'endpoint
  // `/api/multiappt/force-rescan`. Idempotente lato server: i tag già
  // applicati a un event_id non vengono ri-applicati.
  const handleForceRescan = useCallback(async () => {
    if (forceScanning) return;
    setForceScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/multiappt/force-rescan`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      const field = data?.field_populate || {};
      const tag = data?.tag_send || {};
      const tagged = tag.tagged || 0;
      const excluded = tag.excluded || 0;
      const populated = field.populated || 0;
      toast.success(
        `Scansione forzata completata · ${tagged} tag, ${excluded} escludi, ${populated} field popolati`,
      );
      // Reload log + cron status
      loadLog?.();
      loadCronStatus?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
        || "Errore scansione forzata — riprova",
      );
    } finally {
      setForceScanning(false);
    }
  // loadLog/loadCronStatus sono callback stabili definite sopra: includerle
  // espliciamente come deps causerebbe loop. La regola eslint-react/exhaustive-deps
  // qui dà falsi positivi.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceScanning]);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/multiappt/log`, {
        params: { limit: 50 }, withCredentials: true,
      });
      setTagLog(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      // non-admin users get 403 — log at debug level only
      console.debug("loadLog:", e?.response?.status, e?.message);
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/multiappt/cron-status`, {
        withCredentials: true,
      });
      setCronStatus(data || null);
    } catch (e) {
      console.debug("loadCronStatus:", e?.response?.status, e?.message);
    }
  }, []);

  const handleClearLog = async () => {
    if (!window.confirm("Cancellare TUTTO lo storico tag inviati? L'azione è irreversibile.")) return;
    try {
      const { data } = await axios.delete(`${API}/multiappt/log`, {
        withCredentials: true,
      });
      toast.success(`Storico cancellato: ${data?.deleted ?? 0} record eliminati`);
      setTagLog([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore cancellazione storico");
    }
  };

  useEffect(() => {
    loadLog();
    loadCronStatus();
  }, [loadLog, loadCronStatus]);

  // Apre il Dialog "Test invio diretto" pre-popolato con il contatto di
  // un gruppo e il testo costruito dai suoi appuntamenti REALI (formato
  // identico a quello che il cron scrive nel custom field).
  const handleDirectTestFromGroup = (group) => {
    const appts = Array.isArray(group?.appointments) ? group.appointments : [];
    const lines = appts
      .map((a) => ({ time: formatTime(a.startTime), doctor: a.doctor_name || "il nostro team" }))
      .filter((x) => x.time && x.time !== "--:--")
      .sort((a, b) => a.time.localeCompare(b.time))
      .map((x) => `• ore ${x.time} con ${x.doctor}`)
      .join("\n");
    setDirectSearch(group.patient_name || "");
    setDirectResults([]);
    setDirectSelected({ id: group.contact_id, name: group.patient_name || group.contact_id });
    setDirectCustomValue(lines);
    setDirectTestOpen(true);
  };

  // --- Direct test: cerca contatto + invia tag + custom field ---
  useEffect(() => {
    if (!directTestOpen) return;
    if (directSearchTimer.current) clearTimeout(directSearchTimer.current);
    const q = (directSearch || "").trim();
    if (q.length < 2) {
      setDirectResults([]);
      setDirectSearching(false);
      return;
    }
    setDirectSearching(true);
    directSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/ghl/contacts/search`, {
          params: { query: q },
          withCredentials: true,
        });
        const items = Array.isArray(data?.contacts) ? data.contacts : (Array.isArray(data) ? data : []);
        setDirectResults(items.slice(0, 20));
      } catch (e) {
        console.debug("direct search:", e?.message);
        setDirectResults([]);
      } finally {
        setDirectSearching(false);
      }
    }, 350);
    return () => directSearchTimer.current && clearTimeout(directSearchTimer.current);
  }, [directSearch, directTestOpen]);

  const resetDirectTest = () => {
    setDirectTestOpen(false);
    setDirectSearch("");
    setDirectResults([]);
    setDirectSelected(null);
    setDirectCustomValue("");
    setDirectSubmitting(false);
  };

  const submitDirectTest = async () => {
    if (!directSelected?.id) return;
    setDirectSubmitting(true);
    // Apre subito il dialog di verifica in stato loading.
    setVerifyDialog({
      open: true,
      loading: true,
      patient: directSelected.name || directSelected.id,
      contactId: directSelected.id,
      value: null,
      hasTag: false,
      error: null,
      customFieldSet: null,
      tagSent: null,
    });
    let tagSent = false;
    let cfSet = null;
    let cfErr = null;
    try {
      const { data } = await axios.post(
        `${API}/multiappt/test-direct`,
        {
          contact_id: directSelected.id,
          custom_value: directCustomValue || null,
        },
        { withCredentials: true }
      );
      tagSent = !!data?.tag_sent;
      cfSet = !!data?.custom_field_set;
      cfErr = data?.custom_field_error || data?.tag_error || null;
      loadLog();
    } catch (err) {
      setVerifyDialog((d) => ({
        ...d,
        loading: false,
        error: err.response?.data?.detail || err.message || "Errore test invio",
      }));
      setDirectSubmitting(false);
      return;
    }
    // Legge il valore live dal contatto GHL.
    try {
      const { data } = await axios.get(
        `${API}/multiappt/contact-field/${encodeURIComponent(directSelected.id)}`,
        { withCredentials: true }
      );
      setVerifyDialog({
        open: true,
        loading: false,
        patient: directSelected.name || directSelected.id,
        contactId: directSelected.id,
        value: data?.value ?? null,
        hasTag: !!data?.has_tag,
        error: data?.ok ? null : (data?.error || "Lettura GHL fallita"),
        customFieldSet: cfSet,
        customFieldError: cfErr,
        tagSent,
      });
    } catch (err) {
      setVerifyDialog((d) => ({
        ...d,
        loading: false,
        tagSent,
        customFieldSet: cfSet,
        customFieldError: cfErr,
        error: err.response?.data?.detail || err.message || "Lettura GHL fallita",
      }));
    } finally {
      setDirectSubmitting(false);
      resetDirectTest();
    }
  };

  const fetchData = useCallback(async (currentDays, { showLoading = false, silent = false, force = false } = {}) => {
    // Cancel any in-flight request (avoids race conditions on fast dropdown changes)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (showLoading) setLoading(true);
    if (!silent) setRefreshing(true);
    try {
      const { data: resp } = await axios.get(`${API}/appointments/same-day-duplicates`, {
        params: {
          days: currentDays,
          _t: Date.now(),
          ...(force ? { force: 1 } : {}),
        },
        withCredentials: true,
        signal: controller.signal,
      });
      setData(resp);
      setLastRefresh(new Date());
      if (!silent) {
        toast.success(
          `${resp.total_groups || 0} paziente/i con appuntamenti multipli in ${currentDays} giorno/i`
        );
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") return; // ignore aborts
      console.error("fetchDuplicates:", err);
      if (!silent) toast.error("Errore durante la scansione");
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // Dropdown change → immediate scan (uses the new `days` value)
  useEffect(() => {
    fetchData(days, { showLoading: !data, silent: true });
    // Silent auto-refresh every 60s
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => fetchData(days, { silent: true }), 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const handleManualRefresh = () => {
    fetchData(days, { force: true });
  };

  const groups = data?.groups || [];

  // Group by date for section headers
  const groupedByDate = groups.reduce((acc, g) => {
    (acc[g.date] = acc[g.date] || []).push(g);
    return acc;
  }, {});
  const dateKeys = Object.keys(groupedByDate).sort();

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4">
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Multiappuntamenti"
          subtitle="Pazienti con 2 o più appuntamenti nello stesso giorno"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setDiagOpen(true); setDiagResult(null); }}
                data-testid="diagnose-btn"
                title="Diagnostica perché il cron non ha taggato un paziente specifico"
                aria-label="Diagnostica paziente"
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-indigo-500/90 hover:bg-indigo-500 border border-indigo-300 text-white text-xs font-semibold transition-colors shadow-sm"
              >
                <Search className="w-3.5 h-3.5" />
                Diagnostica
              </button>
              <button
                type="button"
                onClick={handleForceRescan}
                disabled={forceScanning}
                data-testid="force-rescan-btn"
                title="Esegui SUBITO una scansione completa cron (utile se un paziente è sfuggito)"
                aria-label="Forza scansione cron"
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-amber-500/90 hover:bg-amber-500 border border-amber-300 text-white text-xs font-semibold transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Zap className={`w-3.5 h-3.5 ${forceScanning ? "animate-pulse" : ""}`} />
                {forceScanning ? "Scansione…" : "Forza scansione"}
              </button>
              <button
                type="button"
                onClick={handleManualRefresh}
                disabled={refreshing}
                data-testid="refresh-btn"
                title="Aggiorna scansione"
                aria-label="Aggiorna"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing || loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          }
        />
      </div>
      <div className="max-w-6xl mx-auto mb-6 -mt-8 relative z-10">
        {/* Filter + stats */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-1">
            <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="range-select"
              className="h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-[#3B42DD] font-medium"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-amber-100 border border-amber-200 rounded-lg px-3 py-1.5">
              <Users className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-xs font-bold text-amber-800" data-testid="total-groups">
                {data?.total_groups || 0} pazienti duplicati
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5">
              <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-bold text-slate-600">
                {data?.total_duplicates || 0} app.ti coinvolti
              </span>
            </div>
          </div>
        </div>

        {lastRefresh && (
          <p className="text-slate-400 text-xs mt-2">
            Ultimo aggiornamento: {lastRefresh.toLocaleTimeString("it-IT")} &middot; auto-refresh ogni 60s
          </p>
        )}
      </div>

      {/* Test mode panel + audit log (above the date results) */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-4" data-testid="multiappt-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-[#1a1fa8]" />
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                Automatismo &laquo;multiappuntamento&raquo;
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDirectTestOpen(true)}
                className="rounded-lg h-8 text-xs"
                data-testid="multiappt-direct-test-btn"
                title="Apri test diretto: cerca un contatto GHL e invia subito tag + custom field"
              >
                <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
                Test invio diretto
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Il <strong>cron automatico è sempre attivo</strong>. I pazienti con
            appuntamenti multipli nello stesso giorno ricevono due automatismi
            <strong> il giorno prima</strong>:
            <br />
            <span className="inline-block mt-1.5 ml-1">
              <strong>1.</strong> A <strong>26 ore prima</strong> del primo appuntamento del giorno
              &mdash; popolato il custom field <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">contact.multiappuntamento</code> con l&apos;elenco delle visite e applicato al contatto GHL il tag <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">multiappuntamento</code>.
            </span>
            <br />
            <span className="inline-block ml-1">
              <strong>2.</strong> A <strong>24 ore e 10 minuti prima</strong> del 2&deg; e successivi appuntamenti dello stesso giorno &mdash; applicato il tag <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">escludi</code> al contatto GHL (solo se il contatto ha gi&agrave; <code>multiappuntamento</code>).
              <br />
              <span className="ml-4 text-slate-400">
                Ogni giorno &egrave; indipendente. Per i giorni successivi il flusso ricomincia.
              </span>
            </span>
            <br />
            Per testare manualmente l&apos;invio su un singolo paziente senza attendere il cron, usa il bottone &laquo;<strong>Test invio diretto</strong>&raquo; sopra (NON sospende n&eacute; influenza il cron).
          </p>

          {/* Cron status — ultimo run + prossimo run di ciascun cron */}
          {cronStatus && (
            <div className="border-t border-slate-100 pt-4" data-testid="cron-status-card">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  Stato automatismi (cron)
                </h4>
                <button
                  type="button"
                  onClick={loadCronStatus}
                  className="text-[10px] uppercase tracking-wide text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-1 rounded transition flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Aggiorna
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: "field_populate", title: "Custom field + tag (26h prima 1° appt)", color: "sky" },
                  { key: "tag_send", title: "Tag escludi (24h10min prima 2°/N appt)", color: "emerald" },
                ].map(({ key, title, color }) => {
                  const c = cronStatus[key] || {};
                  const lastIso = c.last_ran_at;
                  const nextIso = c.next_run;
                  const stats = c.stats || {};
                  const skipped = stats.skipped === true || !!stats.reason;
                  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", {
                    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                  }) : "—";
                  return (
                    <div key={key} className={`rounded-xl border border-slate-200 bg-${color}-50/40 p-3`} data-testid={`cron-card-${key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-[11px] font-bold uppercase tracking-wide text-${color}-700`}>{title}</span>
                        {c.error ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-red-600">
                            <XCircle className="w-3 h-3" /> Errore
                          </span>
                        ) : skipped ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-600">
                            Skipped
                          </span>
                        ) : lastIso ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase text-slate-400">In attesa</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-600 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Ultima esecuzione</span>
                          <span className="font-medium tabular-nums">{fmt(lastIso)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Prossima esecuzione</span>
                          <span className="font-medium tabular-nums">{fmt(nextIso)}</span>
                        </div>
                        {c.error && (
                          <div className="text-red-600 text-[10px] mt-1 break-words">⚠ {c.error}</div>
                        )}
                        {!c.error && stats.reason && (
                          <div className="text-amber-700 text-[10px] mt-1">Motivo: {stats.reason}</div>
                        )}
                        {!c.error && !stats.reason && lastIso && (
                          <div className="text-slate-500 text-[10px] mt-1 font-mono">
                            {key === "field_populate" ? (
                              <>checked={stats.checked ?? 0} · populated={stats.populated ?? 0} · skipped={stats.skipped ?? 0}</>
                            ) : (
                              <>checked={stats.checked ?? 0} · tagged={stats.tagged ?? 0} · excluded={stats.excluded ?? 0} · out_win={stats.skipped_out_window ?? 0}</>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">
                Pianificazione: {cronStatus.field_populate?.schedule_label} · {cronStatus.tag_send?.schedule_label}
              </p>
            </div>
          )}

          {/* Audit log */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" />
                Storico tag inviati
                <span className="text-[10px] text-slate-400 font-normal normal-case">
                  (ultimi 50)
                </span>
              </h4>
              {tagLog.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearLog}
                  data-testid="clear-tag-log-btn"
                  className="text-[11px] font-semibold uppercase tracking-wide text-red-600 hover:text-red-700 hover:bg-red-50 px-2.5 py-1 rounded transition flex items-center gap-1"
                  title="Cancella tutto lo storico"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Svuota storico
                </button>
              )}
            </div>
            {tagLog.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nessun tag inviato finora.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="audit-log-table">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                    <tr className="border-b border-slate-100">
                      <th className="text-left font-semibold py-2 px-2">Data invio</th>
                      <th className="text-left font-semibold py-2 px-2">Paziente</th>
                      <th className="text-left font-semibold py-2 px-2">Giorno appt.</th>
                      <th className="text-left font-semibold py-2 px-2">Ora appt.</th>
                      <th className="text-left font-semibold py-2 px-2">Tag</th>
                      <th className="text-center font-semibold py-2 px-2">Mode</th>
                      <th className="text-center font-semibold py-2 px-2">Esito</th>
                      <th className="text-left font-semibold py-2 px-2">Trigger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagLog.map((row, i) => (
                      <tr key={`${row.contact_id}-${row.tagged_at}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-2 px-2 text-slate-700 tabular-nums">
                          {row.tagged_at ? new Date(row.tagged_at).toLocaleString("it-IT", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          }) : "—"}
                        </td>
                        <td className="py-2 px-2 text-slate-700 truncate max-w-[200px]">
                          {row.contact_name || "—"}
                        </td>
                        <td className="py-2 px-2 text-slate-600">{row.appointment_date}</td>
                        <td className="py-2 px-2 text-slate-700 tabular-nums font-medium">
                          {row.event_time || "—"}
                        </td>
                        <td className="py-2 px-2">
                          {row.tag ? (
                            <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                              row.tag === "escludi"
                                ? "bg-rose-100 text-rose-700"
                                : row.tag === "multiappuntamento"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                            }`}>
                              {row.tag}
                            </span>
                          ) : row.phase === "field" ? (
                            <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-100 text-sky-700" title="Custom field popolato (step 26h prima del 1° appt)">
                              field
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            row.mode === "test"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-blue-100 text-blue-700"
                          }`}>
                            {row.mode}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center">
                          {row.success ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                          ) : (
                            <span title={row.error || ""} className="inline-flex items-center text-red-500">
                              <XCircle className="w-4 h-4" />
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-slate-500 text-[11px]">{row.triggered_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto">
        {loading && !data ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
            <p className="text-slate-400">Analisi appuntamenti in corso…</p>
            <p className="text-slate-300 text-xs mt-1">(può richiedere qualche secondo)</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <Users className="w-12 h-12 text-emerald-200 mx-auto mb-3" />
            <p className="text-slate-700 text-lg font-medium">Nessun paziente con appuntamenti duplicati</p>
            <p className="text-slate-400 text-sm mt-1">
              Nel periodo selezionato ogni paziente ha massimo 1 appuntamento al giorno
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {dateKeys.map((date) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500 px-2">
                    {formatDate(date)}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {groupedByDate[date].length}
                  </span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="space-y-3">
                  {groupedByDate[date].map((g) => (
                    <GroupCard
                      key={`${g.contact_id}-${g.date}`}
                      group={g}
                      onDirectTest={handleDirectTestFromGroup}
                      taggedKey={recentlyTagged}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog: TEST INVIO DIRETTO — indipendente dai gruppi reali.
          Cerca un contatto GHL e invia subito tag + custom field con un
          payload di esempio (o testo personalizzato). */}
      <Dialog
        open={directTestOpen}
        onOpenChange={(open) => !open && resetDirectTest()}
      >
        <DialogContent className="max-w-lg" data-testid="multiappt-direct-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#1a1fa8]" />
              Test invio diretto
            </DialogTitle>
            <DialogDescription>
              Cerca un contatto GHL e invia subito il tag <code>multiappuntamento</code> con un payload di esempio. Funziona anche se il contatto non ha appuntamenti multipli reali.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Search */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Contatto GHL
              </label>
              <Input
                placeholder="Cerca per nome, telefono o email&hellip;"
                value={directSearch}
                onChange={(e) => { setDirectSearch(e.target.value); setDirectSelected(null); }}
                data-testid="multiappt-direct-search-input"
                autoFocus
              />
              {directSearching && (
                <div className="text-xs text-slate-400 mt-1">Ricerca in corso&hellip;</div>
              )}
              {!directSearching && directSearch.length >= 2 && directResults.length === 0 && !directSelected && (
                <div className="text-xs text-slate-400 mt-1">Nessun contatto trovato</div>
              )}
            </div>

            {/* Results list */}
            {!directSelected && directResults.length > 0 && (
              <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {directResults.map((c) => {
                  const name = c.contactName || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "—";
                  return (
                    <button
                      key={c.id || c.ghl_id}
                      type="button"
                      onClick={() => setDirectSelected({ id: c.id || c.ghl_id, name })}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center justify-between gap-2"
                      data-testid={`multiappt-direct-result-${c.id || c.ghl_id}`}
                    >
                      <span className="font-semibold text-slate-700 truncate">{name}</span>
                      <span className="text-[10px] text-slate-400 font-mono truncate ml-2">{c.id || c.ghl_id}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selected contact */}
            {directSelected && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Selezionato</div>
                  <div className="text-sm font-semibold text-emerald-900 truncate">{directSelected.name}</div>
                  <div className="text-[10px] text-emerald-600 font-mono truncate">{directSelected.id}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setDirectSelected(null)}
                  className="text-xs font-semibold text-emerald-700 hover:underline"
                >
                  cambia
                </button>
              </div>
            )}

            {/* Custom value override */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Testo per <code>{`{{contact.multiappuntamento}}`}</code> (opzionale)
              </label>
              <Textarea
                placeholder={"• ore 09:30 con Dott. Bianchi\n• ore 14:00 con Dott.ssa Verdi"}
                value={directCustomValue}
                onChange={(e) => setDirectCustomValue(e.target.value)}
                rows={3}
                className="font-mono text-xs"
                data-testid="multiappt-direct-value-input"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Se lasci vuoto, viene inviato il payload di esempio mostrato sopra.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={resetDirectTest}>
              Annulla
            </Button>
            <Button
              type="button"
              onClick={submitDirectTest}
              disabled={!directSelected || directSubmitting}
              className="bg-[#1a1fa8] hover:bg-[#13167e] text-white"
              data-testid="multiappt-direct-submit-btn"
            >
              {directSubmitting ? "Invio in corso…" : "Invia test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: esito completo del Test Tag — mostra anche cosa è stato
          effettivamente scritto nel custom field GHL `multiappuntamento`. */}
      <Dialog
        open={!!verifyDialog?.open}
        onOpenChange={(open) => !open && setVerifyDialog(null)}
      >
        <DialogContent className="max-w-lg" data-testid="multiappt-verify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-600" />
              Test Tag &middot; {verifyDialog?.patient || ""}
            </DialogTitle>
            <DialogDescription>
              Verifica end-to-end: tag GHL applicato e custom field <code>contact.multiappuntamento</code> letto in tempo reale.
            </DialogDescription>
          </DialogHeader>

          {verifyDialog?.loading ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="multiappt-verify-loading">
              Invio tag e verifica in corso&hellip;
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              {/* Tag status */}
              <div className="flex items-start gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                {verifyDialog?.tagSent ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="font-semibold text-slate-700">
                    Tag <code>multiappuntamento</code>: {verifyDialog?.tagSent ? "applicato" : "non applicato"}
                  </div>
                  {verifyDialog?.hasTag && (
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Confermato sul contatto GHL.
                    </div>
                  )}
                </div>
              </div>

              {/* Custom field status */}
              <div className="flex items-start gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                {verifyDialog?.customFieldSet ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="font-semibold text-slate-700">
                    Custom field GHL scritto: {verifyDialog?.customFieldSet ? "sì" : "no"}
                  </div>
                  {verifyDialog?.customFieldError && (
                    <div className="text-xs text-red-700 mt-0.5">
                      {verifyDialog.customFieldError}
                    </div>
                  )}
                </div>
              </div>

              {/* Valore letto live da GHL */}
              <div className="bg-slate-900 rounded-lg px-3 py-3 border border-slate-700">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                  Valore attuale di <code>{`{{contact.multiappuntamento}}`}</code>
                </div>
                {verifyDialog?.error ? (
                  <div className="text-sm text-red-300" data-testid="multiappt-verify-error">{verifyDialog.error}</div>
                ) : verifyDialog?.value ? (
                  <pre className="text-sm text-slate-100 whitespace-pre-wrap font-mono leading-relaxed" data-testid="multiappt-verify-value">
                    {verifyDialog.value}
                  </pre>
                ) : (
                  <div className="text-sm text-slate-400 italic">
                    (campo vuoto su GHL)
                  </div>
                )}
              </div>

              {verifyDialog?.contactId && (
                <div className="text-[11px] text-slate-400 font-mono">
                  contactId: {verifyDialog.contactId}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setVerifyDialog(null)}
              data-testid="multiappt-verify-close-btn"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diagnostica paziente: spiega perché il cron NON ha applicato i
          tag a uno specifico paziente nel giorno target. Chiama l'endpoint
          già esistente `/api/multiappt/diagnose` e mostra in modo leggibile
          events_in_day + verdict + log_entries recenti. */}
      <Dialog open={diagOpen} onOpenChange={(o) => { if (!o) { setDiagOpen(false); setDiagResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="multiappt-diagnose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-indigo-600" />
              Diagnostica cron multiappuntamento
            </DialogTitle>
            <DialogDescription>
              Inserisci il nome del paziente (o l'ID contatto GHL) per scoprire perché il cron non ha applicato i tag. Default: oggi.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_auto] gap-2 items-end">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nome o contact_id GHL</label>
                <Input
                  value={diagQuery}
                  onChange={(e) => setDiagQuery(e.target.value)}
                  placeholder="es. dolfi"
                  data-testid="diagnose-query-input"
                  onKeyDown={(e) => { if (e.key === "Enter") handleDiagnose(); }}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Data (default oggi)</label>
                <Input
                  type="date"
                  value={diagDate}
                  onChange={(e) => setDiagDate(e.target.value)}
                  data-testid="diagnose-date-input"
                />
              </div>
              <Button
                type="button"
                onClick={handleDiagnose}
                disabled={diagLoading || !diagQuery.trim()}
                data-testid="diagnose-submit-btn"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {diagLoading ? "Analisi…" : "Diagnostica"}
              </Button>
            </div>

            {diagResult && (
              <div className="mt-4 space-y-3 text-sm" data-testid="diagnose-result">
                {/* Verdict box */}
                <div className={`p-3 rounded-lg border ${
                  diagResult.verdict === "would_have_worked"
                    ? "bg-amber-50 border-amber-200"
                    : diagResult.verdict === "already_logged"
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-rose-50 border-rose-200"
                }`}>
                  <div className="font-bold text-slate-800 mb-1">Verdetto: <code className="bg-white px-2 py-0.5 rounded text-xs">{diagResult.verdict}</code></div>
                  {diagResult.verdict_extra?.explain && (
                    <div className="text-xs text-slate-700 whitespace-pre-wrap">{diagResult.verdict_extra.explain}</div>
                  )}
                  {diagResult.verdict_extra?.appts_count != null && (
                    <div className="text-xs text-slate-700 mt-1">Appuntamenti trovati nel giorno: <b>{diagResult.verdict_extra.appts_count}</b></div>
                  )}
                  {diagResult.verdict_extra?.last_log && (
                    <div className="text-xs text-slate-700 mt-2">
                      <b>Ultimo log:</b> {diagResult.verdict_extra.last_log.tag || "—"} @ {new Date(diagResult.verdict_extra.last_log.tagged_at).toLocaleString("it-IT")}
                    </div>
                  )}
                </div>

                {/* Contact info */}
                {diagResult.contact && (
                  <div className="text-xs text-slate-600">
                    <b>Contatto:</b> {diagResult.contact.name || "—"} · <code>{diagResult.contact.id}</code>
                  </div>
                )}

                {/* Events list */}
                {Array.isArray(diagResult.events_in_day) && diagResult.events_in_day.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Appuntamenti del giorno {diagResult.target_date} ({diagResult.events_in_day.length}):</div>
                    <div className="border rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                      {diagResult.events_in_day.map((ev, i) => (
                        <div key={ev.event_id || i} className="px-3 py-2 text-xs flex items-center justify-between bg-white">
                          <div>
                            <span className="font-semibold tabular-nums">{ev.start_hhmm}</span>
                            <span className="text-slate-500 ml-2">{ev.title || "—"}</span>
                          </div>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            (ev.status || "").toLowerCase() === "confirmed"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {ev.status || "?"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent log entries */}
                {Array.isArray(diagResult.log_entries) && diagResult.log_entries.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Ultimi {diagResult.log_entries.length} log per questo contatto:</div>
                    <div className="border rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
                      {diagResult.log_entries.map((le, i) => (
                        // Log entries don't expose a stable ID; tagged_at + tag is unique enough
                        // for the small list (max 50). Fallback to `i` only for legacy entries.
                        <div key={`${le.tagged_at || "?"}-${le.tag || le.phase || i}`} className="px-3 py-2 text-xs bg-white">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold tabular-nums">
                              {new Date(le.tagged_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              {" · "}
                              <code className="bg-slate-100 px-1">{le.tag || le.phase || "—"}</code>
                              {le.event_time && <span className="text-slate-500 ml-1">@{le.event_time}</span>}
                            </span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${le.success ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                              {le.success ? "OK" : "FAIL"}
                            </span>
                          </div>
                          {le.error && <div className="text-rose-600 text-[11px] mt-1 whitespace-pre-wrap">{le.error}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setDiagOpen(false); setDiagResult(null); }}
              data-testid="diagnose-close-btn"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
